import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  inject
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Menu, MenuService } from '@delon/theme';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { Subject, filter, takeUntil } from 'rxjs';

import { TestIdDirective } from '../../directives/test-id.directive';

@Component({
  selector: 'custom-sidebar-nav',
  standalone: true,
  imports: [CommonModule, NzIconModule, NzToolTipModule, NzBadgeModule, TestIdDirective],
  template: `
    <ng-template #icon let-i>
      @if (i) {
        @switch (i.type) {
          @case ('icon') {
            <nz-icon
              class="sidebar-nav__item-icon"
              [nzType]="i.value"
              [nzTheme]="i.theme"
              [nzSpin]="i.spin"
              [nzTwotoneColor]="i.twoToneColor"
              [nzIconfont]="i.iconfont"
              [nzRotate]="i.rotate"
            />
          }
          @case ('iconfont') {
            <nz-icon class="sidebar-nav__item-icon" [nzIconfont]="i.iconfont" />
          }
          @case ('img') {
            <img [src]="i.value" class="sidebar-nav__item-icon sidebar-nav__item-img" />
          }
          @case ('svg') {
            <span class="sidebar-nav__item-icon sidebar-nav__item-svg" [innerHTML]="i.value"></span>
          }
          @default {
            <i class="sidebar-nav__item-icon {{ i.value }}"></i>
          }
        }
      }
    </ng-template>

    <ng-template #tree let-ls>
      @for (i of ls; track $index) {
        @if (i._hidden !== true) {
          <li
            class="sidebar-nav__item"
            [class.sidebar-nav__selected]="i._selected"
            [class.sidebar-nav__open]="i.open"
            [class.sidebar-nav__item--official-bottom]="i.testId === 'menu-official-tenant-management'"
          >
            <!-- link -->
            @if (!i.children || i.children.length === 0) {
              <a
                (click)="to(i)"
                [testId]="i.testId || 'menu-item-' + i._id"
                class="sidebar-nav__item-link"
                [class.sidebar-nav__item-disabled]="i.disabled"
                (mouseenter)="closeSubMenu()"
              >
                @if (i.icon) {
                  @if (collapsed) {
                    <span nz-tooltip nzTooltipPlacement="right" [nzTooltipTitle]="i.text">
                      <ng-template [ngTemplateOutlet]="icon" [ngTemplateOutletContext]="{ $implicit: i.icon }" />
                    </span>
                  } @else {
                    <ng-template [ngTemplateOutlet]="icon" [ngTemplateOutletContext]="{ $implicit: i.icon }" />
                  }
                }
                <span class="sidebar-nav__item-text" [innerHTML]="i.text" [attr.title]="i.text"></span>
              </a>
            }
            <!-- has children link -->
            @if (i.children && i.children.length > 0) {
              <a
                (click)="toggleOpen(i)"
                (mouseenter)="showSubMenu($event, i)"
                class="sidebar-nav__item-link"
                [testId]="i.testId || 'menu-item-' + i._id"
              >
                <ng-template [ngTemplateOutlet]="icon" [ngTemplateOutletContext]="{ $implicit: i.icon }" />
                <span class="sidebar-nav__item-text" [innerHTML]="i.text" [attr.title]="i.text"></span>
                <i class="sidebar-nav__sub-arrow"></i>
              </a>
            }
            <!-- badge -->
            @if (i.badge) {
              <nz-badge
                [nzCount]="i.badge"
                [nzDot]="i.badgeDot"
                nzStandalone
                [nzOverflowCount]="i.badgeOverflowCount ? i.badgeOverflowCount : 9"
              />
            }
            @if (i.children && i.children.length > 0) {
              <ul class="sidebar-nav sidebar-nav__sub sidebar-nav__depth{{ i._depth }}">
                <ng-template [ngTemplateOutlet]="tree" [ngTemplateOutletContext]="{ $implicit: i.children }" />
              </ul>
            }
          </li>
        }
      }
    </ng-template>

    <ul class="sidebar-nav">
      @for (group of list; track $index) {
        @if (group.group) {
          <li class="sidebar-nav__item sidebar-nav__group-title">
            <span [innerHTML]="group.text"></span>
          </li>
        }
        <ng-template [ngTemplateOutlet]="tree" [ngTemplateOutletContext]="{ $implicit: group.children }" />
      }
    </ul>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class CustomSidebarNavComponent implements OnInit, OnDestroy {
  private readonly menuService = inject(MenuService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  @Input() collapsed = false;
  @Output() readonly select = new EventEmitter<Menu>();

  list: Menu[] = [];

  ngOnInit(): void {
    this.menuService.change.pipe(takeUntil(this.destroy$)).subscribe(data => {
      this.list = data;
      this.syncMenuState(this.router.url);
    });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(event => {
        this.syncMenuState((event as NavigationEnd).urlAfterRedirects);
      });

    this.list = this.menuService.menus;
    this.syncMenuState(this.router.url);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  to(item: Menu): void {
    if (item.disabled) return;

    if (item.externalLink) {
      window.open(item.link, '_blank', 'noopener,noreferrer');
      return;
    }

    this.select.emit(item);

    if (item.link) {
      this.router.navigateByUrl(item.link);
    }
  }

  toggleOpen(item: Menu): void {
    item.open = !item.open;
    this.cdr.detectChanges();
  }

  showSubMenu(event: MouseEvent, item: Menu): void {
    // 实现子菜单显示逻辑
  }

  closeSubMenu(): void {
    // 实现关闭子菜单逻辑
  }

  private syncMenuState(url: string): void {
    const currentUrl = this.normalizeUrl(url);
    this.list = this.markMenus(this.list, currentUrl);
    this.cdr.markForCheck();
  }

  private markMenus(menus: Menu[], currentUrl: string): Menu[] {
    return menus.map(menu => this.markMenu(menu, currentUrl));
  }

  private markMenu(menu: Menu, currentUrl: string): Menu {
    const children = menu.children?.map(child => this.markMenu(child, currentUrl)) ?? [];
    const selfSelected = this.isMenuSelected(menu, currentUrl);
    const childSelected = children.some(child => child['_selected'] || child.open);

    return {
      ...menu,
      children,
      _selected: selfSelected || childSelected,
      open: childSelected || !!menu.open
    };
  }

  private isMenuSelected(menu: Menu, currentUrl: string): boolean {
    if (!menu.link) {
      return false;
    }

    const link = this.normalizeUrl(menu.link);
    return currentUrl === link || currentUrl.startsWith(`${link}/`);
  }

  private normalizeUrl(url: string): string {
    return url.split('?')[0].replace(/\/+$/, '') || '/';
  }
}
